import { Controller } from '@hotwired/stimulus';
import { DogNumberValidation } from './mixins/dog_number_validation';

export default class extends Controller {
  static targets = [
    'numberInput',
    'dogLabel',
    'nameField',
    'andSpan',
    'submitSection',
    'nameFieldsContainer',
  ];

  connect() {
    // Attach input listeners to name fields for dynamic updates
    this.nameFieldTargets.forEach((field) => {
      field.addEventListener('input', () => this.update());
    });
    this.numberInputTarget.addEventListener('input', () => {
      DogNumberValidation.validateAndShowError(
        this.numberInputTarget,
        this.element,
        'max-dogs-error'
      );
      this.update();
      this.enableContinue();
    });
    this.update();
    this.enableContinue();

    // Attach submit listener to form for PostHog capture and validation
    const form = this.element.closest('form');
    if (form) {
      form.addEventListener('submit', (e) => this.handleSubmit(e));
    }
  }

  // Helper to get the current number of name fields
  get maxDogs() {
    return this.nameFieldTargets.length;
  }

  // Prevent Enter key from submitting the form in input fields
  preventEnterSubmit(event) {
    if (event.key === 'Enter' || event.keyCode === 13) {
      event.preventDefault();
      return false;
    }
  }

  // Check if the form is ready to be submitted
  isFormValid() {
    const num = parseInt(this.numberInputTarget.value, 10);
    const hasNumber = !isNaN(num) && this.numberInputTarget.value.trim() !== '';

    if (!hasNumber) return false;

    // Check if number exceeds max
    if (!DogNumberValidation.isValid(this.numberInputTarget)) {
      return false;
    }

    const $visibleNameFields = this.nameFieldTargets.filter(
      (field) => field.style.display !== 'none'
    );

    // Check if all visible fields are filled
    const allFieldsFilled = $visibleNameFields.every(
      (field) => field.value.trim().length > 0
    );

    // Check for duplicate names
    const hasDuplicateNames =
      new Set(
        $visibleNameFields.map((field) => field.value.trim().toLowerCase())
      ).size !== $visibleNameFields.length;

    // Check if we have the expected number of filled fields
    const hasAllRequiredFields =
      hasNumber && $visibleNameFields.length === num && allFieldsFilled;

    return hasAllRequiredFields && !hasDuplicateNames;
  }

  // Handle form submission
  handleSubmit(event) {
    // Check max dogs validation first
    if (!DogNumberValidation.isValid(this.numberInputTarget)) {
      event.preventDefault();
      DogNumberValidation.showError(this.element, 'max-dogs-error');
      return false;
    }

    if (!this.isFormValid()) {
      event.preventDefault();
      return false;
    }
    this.captureSubmit();
  }

  update() {
    // Validate and show error message if needed
    DogNumberValidation.validateAndShowError(
      this.numberInputTarget,
      this.element,
      'max-dogs-error'
    );

    // Parse the desired number of dogs
    let num = parseInt(this.numberInputTarget.value, 10);
    const hasNumber = !isNaN(num) && this.numberInputTarget.value.trim() !== '';

    // Check for pre-filled values to infer number if not set
    // Skip this when the user is actively editing the number input to avoid overriding backspace/delete
    if (!hasNumber && document.activeElement !== this.numberInputTarget) {
      let filledCount = 0;
      for (let i = 0; i < this.maxDogs; i++) {
        const field = this.nameFieldTargets[i];
        if (field && field.value.trim().length > 0) {
          filledCount = i + 1;
        }
      }
      // If we found pre-filled values, set the number input
      if (filledCount > 0) {
        num = filledCount;
        this.numberInputTarget.value = filledCount;
      }
    }

    // Re-check hasNumber after potentially setting it from pre-filled values
    const hasNumberAfterCheck =
      !isNaN(num) && this.numberInputTarget.value.trim() !== '';

    if (this.hasNameFieldsContainerTarget) {
      this.nameFieldsContainerTarget.style.display = hasNumberAfterCheck
        ? ''
        : 'none';
    }

    // Update dog label (singular/plural)
    if (this.hasDogLabelTarget) {
      // If empty or zero, show "dogs" (plural) to match static text
      if (isNaN(num) || num === 0) {
        this.dogLabelTarget.textContent = 'dogs';
      } else {
        this.dogLabelTarget.textContent = num === 1 ? 'dog' : 'dogs';
      }
    }

    // Default to 1 for field visibility logic
    // Note: We allow values > 5 but cap display at max for showing name fields
    if (isNaN(num) || num < 1) num = 1;
    const displayNum = Math.min(num, DogNumberValidation.MAX_DOGS);

    // Track which fields are visible
    let visibleFields = [];

    for (let i = 0; i < this.maxDogs; i++) {
      const field = this.nameFieldTargets[i];
      if (!field) continue;

      // Show the first field if a number is entered OR if it has a pre-filled value
      if (i === 0 && (hasNumberAfterCheck || field.value.trim().length > 0)) {
        field.style.display = '';
        visibleFields.push(i);
      } else if (i === 0) {
        field.style.display = 'none';
      } else if (i < displayNum) {
        // Only show if previous field is filled (ignoring whitespace)
        const prevField = this.nameFieldTargets[i - 1];
        const prevFieldFilled = prevField && prevField.value.trim().length > 0;
        const thisFieldFilled = field.value.trim().length > 0;

        // Show if previous is filled (sequential input) OR
        // if this field has a value and we're within the requested number (handles pre-filled data)
        if (prevFieldFilled || (thisFieldFilled && i < num && i > 0)) {
          field.style.display = '';
          visibleFields.push(i);
        } else {
          // Hide this and all subsequent fields
          for (let j = i; j < this.maxDogs; j++) {
            const nextField = this.nameFieldTargets[j];
            if (nextField) {
              nextField.style.display = 'none';
            }
          }
          break;
        }
      } else {
        // Hide fields beyond the requested number
        field.style.display = 'none';
      }
    }

    // Hide all ampersands first
    this.andSpanTargets.forEach((span) => (span.style.display = 'none'));

    // Show ampersand before every visible field after the first
    if (visibleFields.length > 1) {
      for (let idx = 1; idx < visibleFields.length; idx++) {
        const i = visibleFields[idx];
        const andSpanIdx = i - 1;
        if (
          andSpanIdx >= 0 &&
          andSpanIdx < this.andSpanTargets.length &&
          this.andSpanTargets[andSpanIdx]
        ) {
          this.andSpanTargets[andSpanIdx].style.display = '';
        }
      }
    }
  }

  enableContinue() {
    const $numberOfDogs = this.numberInputTarget;
    const $submitSection = this.hasSubmitSectionTarget
      ? this.submitSectionTarget
      : null;
    const $submitButton = this.element.querySelector('button[type=submit]');
    const $visibleNameFields = this.nameFieldTargets.filter(
      (field) => field.style.display !== 'none'
    );

    // Parse the desired number of dogs
    const num = parseInt($numberOfDogs.value, 10);
    const hasNumber = !isNaN(num) && $numberOfDogs.value.trim() !== '';

    // Check if number exceeds max
    const exceedsMax = !DogNumberValidation.isValid($numberOfDogs);

    // true if all the visible name fields have a non-empty value
    const allFieldsFilled = $visibleNameFields.every(
      (field) => field.value.trim().length > 0
    );

    // true if any of the visible name fields have the same value
    const hasDuplicateNames =
      new Set(
        $visibleNameFields.map((field) => field.value.trim().toLowerCase())
      ).size !== $visibleNameFields.length;

    // Check if we have the expected number of filled fields
    // Use displayNum (capped at max) for comparison since we only show max fields
    const displayNum = Math.min(num, DogNumberValidation.MAX_DOGS);
    const hasAllRequiredFields =
      hasNumber && $visibleNameFields.length === displayNum && allFieldsFilled;

    if (hasDuplicateNames) {
      this.element.querySelector('#unique-name-error').style.display = 'block';
    } else {
      this.element.querySelector('#unique-name-error').style.display = 'none';
    }

    // Show/hide submit section:
    // - show when the form is otherwise ready OR when we need to display errors (duplicate names / exceeds max)
    if ($submitSection) {
      if (hasAllRequiredFields || hasDuplicateNames || exceedsMax) {
        $submitSection.style.display = '';
      } else {
        $submitSection.style.display = 'none';
      }
    }

    // Disable submit button when invalid (including exceeds max)
    if ($submitButton) {
      if (hasAllRequiredFields && !hasDuplicateNames && !exceedsMax) {
        $submitButton.removeAttribute('disabled');
      } else {
        $submitButton.setAttribute('disabled', 'disabled');
      }
    }
  }

  captureSubmit() {
    // Prevent multiple captures
    if (this.element.dataset.submitCaptured === 'true') return;

    const numDogs = parseInt(this.numberInputTarget.value, 10);
    const hasNumber =
      !isNaN(numDogs) && this.numberInputTarget.value.trim() !== '';

    if (!hasNumber) return;

    if (typeof window.safePosthogCapture === 'function') {
      const captured = window.safePosthogCapture('dog_names_step_one_submit_clicked', {
        number_of_dogs: numDogs,
      });
      if (captured) {
        this.element.dataset.submitCaptured = 'true';
      }
    }
  }
}
